import Dropdown from '@/components/Header/Dropdown';
import ChevronDownIcon from '@/components/Icons/ChevronDown';
import { IHeaderDictionary } from '@/dictionaries/components/header/types';
import { ISlugDictionary } from '@/dictionaries/utils/slugs/types';
import urlDict from '@/dictionaries/utils/url/en';
import useCurrency from '@/hooks/useCurrency';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  lang: Locale;
  atFooter?: boolean;
  dict: IHeaderDictionary;
  slugsDict: ISlugDictionary;
};

const currencyOptions = [
  {
    label: 'Euro',
    value: 'EUR',
    flag: 'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/admin/flags/EU.png',
  },
  {
    label: 'Dollar',
    value: 'USD',
    flag: 'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/admin/flags/US.png',
  },
  {
    label: 'Pound',
    value: 'GBP',
    flag: 'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/admin/flags/GB.png',
  },
];

const langOptions = [
  { label: 'English', value: 'en' },
  { label: 'Italiano', value: 'it' },
  { label: 'Deutsch', value: 'de' },
  { label: 'Français', value: 'fr' },
];

export function HeaderDropdowns({
  lang,
  dict,
  slugsDict,
  atFooter = false,
}: Props) {
  const { currency } = useCurrency();
  const [selectedCurrency, setSelectedCurrency] = useState(currency.value);
  const [selectedLang, setSelectedLang] = useState<string>(lang);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);

  const fullLangDescription = useMemo(
    () =>
      langOptions
        .find((lang) => lang.value === selectedLang)
        ?.label.toLocaleUpperCase(),
    [selectedLang],
  );

  useEffect(() => {
    setSelectedCurrency(currency.value);
  }, [currency]);

  return (
    <>
      <div
        className={cn('flex items-center cursor-pointer gap-1', {
          'text-footer-gray ml-4': atFooter,
        })}
        onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}>
        <span
          className={cn(`text-sm font-normal uppercase lang font-text`, {
            'text-base': atFooter,
          })}>
          {selectedCurrency}
        </span>
        <ChevronDownIcon
          className={`w-6 h-6 transition-transform duration-300 ease-in-out transform ${
            showCurrencyDropdown ? 'rotate-180' : 'rotate-0'
          } currency ${atFooter && 'translate-y-[5px] ml-2'}`}
        />
      </div>
      {showCurrencyDropdown && (
        <Dropdown
          title={dict.select.currencyModalTitle}
          options={currencyOptions}
          selectedValue={selectedCurrency}
          setIsOpen={setShowCurrencyDropdown}
          setSelectedCurrency={setSelectedCurrency}
          setSelectedLang={setSelectedLang}
          type="currency"
          urlDict={urlDict}
          slugsDict={slugsDict}
          atFooter={atFooter}
        />
      )}

      <div
        className={cn('flex items-center cursor-pointer gap-1', {
          'text-footer-gray ml-4': atFooter,
        })}
        onClick={() => setShowLangDropdown(!showLangDropdown)}>
        <span
          className={cn(`text-sm font-normal uppercase lang font-text`, {
            'text-base': atFooter,
          })}>
          {atFooter ? fullLangDescription : selectedLang}
        </span>
        <ChevronDownIcon
          className={`w-6 h-6 transition-transform duration-300 ease-in-out transform ${
            showLangDropdown ? 'rotate-180' : 'rotate-0'
          } lang ${atFooter && 'translate-y-[5px] ml-2'}`}
        />
      </div>
      {showLangDropdown && (
        <Dropdown
          title={dict.select.languageModalTitle}
          options={langOptions}
          selectedValue={selectedLang}
          setIsOpen={setShowLangDropdown}
          setSelectedCurrency={setSelectedCurrency}
          setSelectedLang={setSelectedLang}
          type="lang"
          urlDict={urlDict}
          slugsDict={slugsDict}
          atFooter={atFooter}
        />
      )}
    </>
  );
}
